import { createIntegration } from 'api/MobileAppsAPI';
import { fetchIntegrations } from 'modules/mobileApps/actions';
import {
  FirebaseNotificationType,
  IntegrationFormField,
  IntegrationFormValues,
  useIntegrationFormContext,
} from 'modules/mobileApps/context/integrationForm';
import { ModalType, useModalContext } from 'modules/mobileApps/context/modal';
import { Integration, IntegrationAuthType, IntegrationPlatform } from 'modules/mobileApps/types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, BUTTON_SIZES } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { BasicSelect } from 'ui/selects';
import { spacing } from 'ui/theme';
import { FileDropzoneField, NotificationTypeField } from '../IntegrationForm/components';
import { AuthDetails } from '../IntegrationForm/formParts/AuthDetails';
import { getIsModalOpen } from './utils';

export const CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID = 'configure-unassigned-integration-modal';
const NAME_ERROR_CONTENT = 'Name is required to save integration.';
const PLATFORM_ERROR_CONTENT = 'Platform is required to save integration.';

const createPlatformSelectOption = (platform: IntegrationPlatform) => ({
  id: `platform-${platform.toLowerCase()}`,
  label: platform,
  value: platform,
});

const formatFieldsForSave = async (
  fields: IntegrationFormValues,
  integration: Integration,
): Promise<Integration> => ({
  ...integration,
  ...(fields.authType === IntegrationAuthType.FIREBASE_JSON && {
    androidUseOsNotifications:
      fields.firebaseNotificationType === FirebaseNotificationType.NOTIFICATION,
    privateKey: (await fields.firebaseJsonFile?.text()) || '',
  }),
  ...(fields.authType === IntegrationAuthType.P8 && {
    p8File: fields.p8File,
    keyID: fields.keyID,
    teamID: fields.teamID,
    bundleID: fields.bundleID,
  }),
  authType: fields.authType,
  testPushSuccess: false,
});

export const ConfigureUnassignedIntegrationModal = () => {
  const dispatch = useDispatch();

  const { closeModal, openedModals } = useModalContext();
  const { getValues, handleSubmit, setValue } = useIntegrationFormContext();

  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<IntegrationPlatform>();

  const [nameErrorText, setNameErrorText] = useState('');
  const [platformErrorText, setPlatformErrorText] = useState('');

  const isGcm = platform === IntegrationPlatform.GCM;
  const isApns =
    platform === IntegrationPlatform.APNS || platform === IntegrationPlatform.APNS_SANDBOX;

  const handleClose = () => {
    closeModal(ModalType.ConfigureUnassignedIntegrationModal);
  };

  const handleNameBlur = () => {
    if (!name.length) setNameErrorText(NAME_ERROR_CONTENT);
  };

  const handleSave = handleSubmit(async () => {
    if (!name.length) setNameErrorText(NAME_ERROR_CONTENT);
    if (!platform) setPlatformErrorText(PLATFORM_ERROR_CONTENT);
    if (!name || !platform) return;

    try {
      const formattedIntegration = await formatFieldsForSave(getValues(), { name, platform });
      await createIntegration(formattedIntegration);
    } finally {
      dispatch(fetchIntegrations());
      handleClose();
    }
  });

  /**
   * For simplicity's sake, auto setting auth types to these values
   * as these are the most common auth types per platform and
   * will likely be sufficient for troubleshooting purposes.
   */
  useEffect(() => {
    if (isGcm) setValue(IntegrationFormField.authType, IntegrationAuthType.FIREBASE_JSON);
    if (isApns) setValue(IntegrationFormField.authType, IntegrationAuthType.P8);
  }, [isGcm, isApns]);

  useEffect(() => {
    if (name.length) setNameErrorText('');
    if (platform) setPlatformErrorText('');
  }, [name, platform]);

  return (
    <Modal
      data-test={CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}
      id={CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}
      isModalOpen={getIsModalOpen(openedModals, ModalType.ConfigureUnassignedIntegrationModal)}
      onClose={handleClose}
      withPortal
    >
      <ModalHeader>Configure unassigned integration</ModalHeader>
      <ModalBody>
        <FormField
          errorText={nameErrorText}
          hasError={!!nameErrorText}
          labelText="Integration name"
        >
          <InputText
            data-test={`${CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}-name-input`}
            onBlur={handleNameBlur}
            onChange={setName}
            value={name}
          />
        </FormField>
        <FormField
          errorText={platformErrorText}
          hasError={!!platformErrorText}
          labelText="Platform"
        >
          <BasicSelect
            data-test={`${CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}-platform-select`}
            id={`${CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}-platform-select`}
            isError={!!platformErrorText}
            onSelect={([value]) => setPlatform(value)}
            options={Object.values(IntegrationPlatform).map(createPlatformSelectOption)}
            isFullWidth
          />
        </FormField>
        {isGcm && (
          <>
            <NotificationTypeField
              data-test={`${CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}-notification-type-radio`}
            />
            <FileDropzoneField
              data-test={`${CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}-file-dropzone`}
              accept={{ 'application/json': ['.json'] }}
              labelText="Upload JSON credentials"
              name={IntegrationFormField.firebaseJsonFile}
            />
          </>
        )}
        {isApns && (
          <div style={{ marginTop: spacing.base }}>
            <AuthDetails />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={`${CONFIGURE_UNASSIGNED_INTEGRATION_MODAL_ID}-save-button`}
          onClick={handleSave}
          size={BUTTON_SIZES.LARGE}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
